import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { ALL_TYPES } from "@acs/shared";

// ── AI Creative Station — Yeni UI ───────────────────────────────────────────
// Claude Design'da tasarlanan chat-bazlı üretim arayüzünün React portu.
// Chat GERÇEK backend'e bağlı: POST /api/ai-chat (Claude tool-use ajanı,
// create_ugc_job/create_image_bulk tool'ları) + GET /api/ai-chat/status ile
// gerçek job ilerlemesi (adım, kod, QA sonucu) canlı takip ediliyor.
// Creative Tree ve Formats ekranları da bu üründe (productId) gerçek veriye
// bağlanacak — Formats şu an mock, Tree zaten ayrı sayfada (CreativeTree.tsx)
// gerçek veriyle çalışıyor.

const ACCENT = "#5E6AD2";

function st(css: string): React.CSSProperties {
  const obj: Record<string, string> = {};
  css.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  });
  return obj as React.CSSProperties;
}

function H({
  tag: Tag = "div", baseStyle, hoverStyle, ...rest
}: { tag?: any; baseStyle: React.CSSProperties; hoverStyle?: React.CSSProperties; [k: string]: any }) {
  const [hover, setHover] = useState(false);
  return (
    <Tag
      {...rest}
      style={{ ...baseStyle, ...(hover ? hoverStyle : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    />
  );
}

// ── Tipler ──────────────────────────────────────────────────────────────────
type Screen = "home" | "chat" | "tree" | "brief" | "gallery";
type ChatMsg = { id: number; role: "user" | "agent"; text: string };
type RealJob = {
  kind: string; id: string;
  label: string;
  status: "queued" | "running" | "completed" | "failed";
  stage: string; // insan diline çevrilmiş adım
  code: string | null;
  qa: { pass: boolean; decision: string; issues: string[] } | null;
  error: string | null;
};
type Activity = { time: string; text: string; kind: "import" | "approve" | "qa" | "create" | "result" };
type Insight = { variable: string; text: string; up: boolean; metric: string };

const STAGE_LABEL: Record<string, string> = {
  brief: "Understanding your request", produce: "Creating the clips",
  combine: "Assembling the video", qa2: "Final quality check", done: "Done",
};

const INITIAL_INSIGHTS: Insight[] = [
  { variable: "Audience", text: "Women 35+ responded to selfie-POV videos 10% better than any other group.", up: true, metric: "+10% conversion" },
  { variable: "Scene", text: "Videos that open on an airport scene bring installs at 18% lower cost than office openers.", up: true, metric: "−18% cost" },
  { variable: "Tone", text: "A calm, reassuring voiceover beat the high-energy one for the privacy message.", up: true, metric: "+7% conversion" },
];
const NOT_WORKING: Insight[] = [
  { variable: "Format", text: "Split-screen before/after clips are losing steam — down 9% over three weeks.", up: false, metric: "−9% engagement" },
  { variable: "Message", text: "“One tap, whole family” hasn't produced a single winner across 3 creatives.", up: false, metric: "0 winning" },
  { variable: "Audience", text: "Parents are converting 12% worse than your other audiences on this offer.", up: false, metric: "−12% conversion" },
];
const INITIAL_ACTIVITY: Activity[] = [
  { time: "Yesterday, 16:20", text: "Ad results imported and analysed — 2 new suggestions found.", kind: "import" },
  { time: "Yesterday, 14:05", text: "You approved 3 videos · Airport opener · Offline mode.", kind: "approve" },
  { time: "Mar 12", text: "New batch of 3 creatives generated from your request.", kind: "create" },
];
const ACTIVITY_DOT: Record<Activity["kind"], string> = { result: "#2f9e6b", approve: ACCENT, qa: "#c98a1a", create: "#7b7bd6", import: "#8a8a93" };

const BUILTIN_UGC_TYPES = [
  { name: "Panel", desc: "3 part, konferans sahnesi, uzman otoritesi." },
  { name: "Influencer POV", desc: "4 part, selfie POV gezgin hikayesi." },
  { name: "Podcast", desc: "Bilim insanı röportajı, stüdyo, 3 açı." },
  { name: "Doctor", desc: "Hastane danışma sahnesi, güven verici otorite." },
  { name: "Twitter", desc: "Ekran kaydı formatında sosyal medya thread'i." },
];
const TALKING_HEAD_AVATAR = { name: "Talking Head Avatar", desc: "AI aktör, doğrudan kameraya konuşan script'li video." };
const LONG_STORY = { name: "Long Story", desc: "Çok sahneli, tek karakterli kısa drama reklamı (fal Kling + ElevenLabs)." };
const VOX_AD = { name: "Vox Ad", desc: "Paper-collage animasyonlu explainer reklam (Arcads Seedance 2.0 + VO)." };
const BROLL_AD = { name: "Long-Form B-Roll Ad", desc: "Konuşan aktör + otomatik B-roll sahneleri, tek videoda birleştirilir (Arcads + Modal)." };
const HOOK_BODY_AVATAR = { name: "Hook Body Avatar", desc: "Avatar, ürünün Hook/Body Drive havuzlarından seçilen asset'lerle gerçek video kompozisyonuyla birleştirilir (Modal rembg + ffmpeg)." };
const GRADIENTS = [
  "linear-gradient(160deg,#e9ebf7,#c4c9ea)", "linear-gradient(160deg,#e6eef2,#bcd2dc)",
  "linear-gradient(160deg,#f4ece5,#e4cdba)", "linear-gradient(160deg,#eef1ec,#cdd7c3)",
  "linear-gradient(160deg,#f3e9f0,#dcc2d6)", "linear-gradient(160deg,#f7f0e6,#e8d4b0)",
  "linear-gradient(160deg,#eaeef7,#c6cfe8)", "linear-gradient(160deg,#eef0ec,#d3dcc7)",
];

// ── StudioChat — "Create" route'unun tüm içeriği ──────────────────────────
// Artık bağımsız bir route (/products/:id/studio), kendi sidebar'ı yok —
// ProductLayout'un kalıcı StudioSidebar'ı altında render ediliyor.
export function StudioChat({ productId, productName }: { productId: string; productName?: string }) {
  const [searchParams] = useSearchParams();
  const [input, setInput] = useState(() => searchParams.get("prefill") ?? "");
  const [msgId, setMsgId] = useState(1);
  const [messages, setMessages] = useState<ChatMsg[]>([
    { id: 0, role: "agent", text: "Hi! Tell me what you'd like to make, or pick an idea below. I'll create it, run the quality checks, and only bring you in when it's time to approve — or when results come back." },
  ]);
  const [sending, setSending] = useState(false);
  const [realJobs, setRealJobs] = useState<RealJob[]>([]);
  const [, setActivity] = useState<Activity[]>(INITIAL_ACTIVITY);

  const msgRef = useRef<HTMLDivElement>(null);
  const pollers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  useEffect(() => () => { Object.values(pollers.current).forEach(clearInterval); }, []);

  function scrollBottom() {
    requestAnimationFrame(() => { const el = msgRef.current; if (el) el.scrollTop = el.scrollHeight; });
  }
  useEffect(scrollBottom, [messages]);

  function pushMsg(role: ChatMsg["role"], text: string) {
    setMsgId((id) => id + 1);
    setMessages((prev) => [...prev, { id: msgId, role, text }]);
  }

  function pollJob(job: RealJob) {
    if (pollers.current[job.id]) return;
    pollers.current[job.id] = setInterval(async () => {
      try {
        const r = await api.getChatJobStatus(job.kind, job.id);
        if (!r.ok) return;
        const stage = STAGE_LABEL[r.stage_hint?.[0] ?? ""] ?? r.step ?? "Working…";
        const status: RealJob["status"] = r.status === "completed" ? "completed" : r.status === "failed" ? "failed" : "running";
        setRealJobs((prev) => prev.map((j) => j.id === job.id ? { ...j, status, stage, code: r.code, qa: r.qa, error: r.error } : j));
        if (status === "completed" || status === "failed") {
          clearInterval(pollers.current[job.id]);
          delete pollers.current[job.id];
          if (status === "completed") {
            const qaNote = r.qa && !r.qa.pass ? ` (⚠️ kalite kontrolde bir şey işaretlendi — QA sekmesinden bak)` : "";
            pushMsg("agent", `✅ ${job.label} tamamlandı${r.code ? ` — kod: ${r.code}` : ""}${qaNote}. Galeri'den görebilirsin.`);
            setActivity((prev) => [{ time: "Just now", text: `${job.label} tamamlandı.`, kind: "create" }, ...prev]);
          } else {
            pushMsg("agent", `⚠️ ${job.label} başarısız oldu${r.error ? `: ${r.error}` : ""}.`);
          }
        }
      } catch { /* ağ hatası — bir sonraki tick'te tekrar dener */ }
    }, 3500);
  }

  async function doSend(text: string, isRetry = false) {
    if (!text.trim() || sending) return;
    const t = text.trim();
    if (!isRetry) pushMsg("user", t);
    setInput("");
    setSending(true);
    try {
      const history = [...messages, { id: -1, role: "user" as const, text: t }]
        .map((m) => ({ role: (m.role === "agent" ? "assistant" : "user") as "user" | "assistant", content: m.text }));
      const r = await api.sendChat(productId, productName, history);
      if (r.ok) {
        pushMsg("agent", r.message || "…");
        if (r.created_jobs?.length) {
          const newJobs: RealJob[] = r.created_jobs.map((cj) => ({
            kind: cj.kind, id: cj.id, label: cj.kind === "ugc" ? "Video" : cj.kind === "edit" ? "Edit" : cj.kind === "long_story" ? "Long Story" : cj.kind === "vox_ad" ? "Vox Ad" : cj.kind === "broll_ad" ? "B-Roll Ad" : cj.kind === "avatar" ? "Avatar" : "Image",
            status: "queued", stage: "Understanding your request", code: null, qa: null, error: null,
          }));
          setRealJobs((prev) => [...prev, ...newJobs]);
          newJobs.forEach(pollJob);
        }
      } else {
        pushMsg("agent", `Bir sorun oldu: ${r.error ?? "bilinmeyen hata"}`);
      }
    } catch (e) {
      if (!isRetry) {
        // Tek seferlik otomatik retry — geçici ağ/API hıçkırıkları (rate limit, cold start vs.) için.
        setSending(false);
        await doSend(t, true);
        return;
      }
      const reason = e instanceof Error ? e.message.replace(/^API \d+: /, "") : String(e);
      pushMsg("agent", `Bağlantı sorunu oldu: ${reason || "bilinmeyen hata"}. Tekrar dener misin?`);
    } finally {
      setSending(false);
    }
  }

  const chips = [
    { text: "Make 3 Influencer POV videos for women 35+", onClick: () => doSend("Make 3 Influencer POV videos for women 35+ audience about privacy, testimonial delivery, young age, female, American") },
    { text: "Make a List image for our privacy message", onClick: () => doSend("Make a List type image for our privacy feature, women 35+ audience") },
    { text: "What worked best last week?", onClick: () => doSend("What worked best last week?") },
  ];

  return (
    <ChatScreen
      messages={messages} input={input} setInput={setInput} onSend={() => doSend(input)}
      sending={sending} showChips={messages.length <= 1} chips={chips} msgRef={msgRef} realJobs={realJobs}
    />
  );
}

// ── CHAT / CREATE ────────────────────────────────────────────────────────
function ChatScreen({ messages, input, setInput, onSend, sending, showChips, chips, msgRef, realJobs }: {
  messages: ChatMsg[]; input: string; setInput: (v: string) => void; onSend: () => void; sending: boolean;
  showChips: boolean; chips: { text: string; onClick: () => void }[]; msgRef: React.RefObject<HTMLDivElement>;
  realJobs: RealJob[];
}) {
  return (
    <div style={st("flex:1;display:flex;min-height:0")}>
      <style>{`
        @keyframes dcspin { to { transform: rotate(360deg); } }
        @keyframes dcfade { from { opacity: 0; transform: translateY(7px); } to { opacity: 1; transform: none; } }
        @keyframes dcpulse { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .4; transform: scale(.82); } }
      `}</style>
      <section style={st("flex:1;min-width:0;display:flex;flex-direction:column")}>
        <div style={st("padding:16px 28px;border-bottom:1px solid rgba(20,20,30,.07);display:flex;align-items:center;gap:10px")}>
          <div style={st("font-size:15px;font-weight:600;letter-spacing:-.01em")}>Create</div>
          <div style={st("font-size:13px;color:#9a9aa2")}>Ask for what you want — I'll handle the rest</div>
        </div>

        <div ref={msgRef} style={st("flex:1;overflow-y:auto;padding:26px 28px")}>
          <div style={st("max-width:660px;margin:0 auto;display:flex;flex-direction:column;gap:18px")}>
            {messages.map((m) => (
              m.role === "user" ? (
                <div key={m.id} style={{ ...st("align-self:flex-end;max-width:82%;color:#fff;padding:11px 15px;border-radius:14px 14px 4px 14px;font-size:14px;line-height:1.5;animation:dcfade .28s ease both"), background: ACCENT }}>{m.text}</div>
              ) : (
                <div key={m.id} style={st("align-self:flex-start;max-width:88%;display:flex;gap:11px;animation:dcfade .28s ease both")}>
                  <AgentIcon />
                  <div style={st("font-size:14px;line-height:1.55;color:#2a2a33;padding-top:3px;white-space:pre-wrap")}>{m.text}</div>
                </div>
              )
            ))}
            {sending && (
              <div style={st("align-self:flex-start;display:flex;gap:11px")}>
                <AgentIcon />
                <div style={st("font-size:13px;color:#9a9aa2;padding-top:5px")}>düşünüyor…</div>
              </div>
            )}
          </div>
        </div>

        <div style={st("padding:14px 28px 20px;border-top:1px solid rgba(20,20,30,.07)")}>
          <div style={st("max-width:660px;margin:0 auto")}>
            {showChips && (
              <div style={st("display:flex;flex-wrap:wrap;gap:8px;margin-bottom:11px")}>
                {chips.map((chip) => (
                  <H key={chip.text} tag="button" onClick={chip.onClick}
                    baseStyle={st("padding:7px 12px;border:1px solid rgba(20,20,30,.12);border-radius:8px;background:#fff;color:#3a3a44;font-family:inherit;font-size:12.5px;cursor:pointer;transition:background .12s,border-color .12s")}
                    hoverStyle={st("background:rgba(20,20,30,.05);border-color:rgba(20,20,30,.2)")}
                  >{chip.text}</H>
                ))}
              </div>
            )}
            <div style={st("display:flex;gap:10px;align-items:flex-end;background:#fff;border:1px solid rgba(20,20,30,.14);border-radius:12px;padding:8px 8px 8px 15px;box-shadow:0 1px 2px rgba(20,20,30,.04)")}>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
                placeholder="Describe the video you want, or ask what's working…"
                style={st("flex:1;border:none;outline:none;background:transparent;font-size:14px;color:#14141b;padding:6px 0")}
              />
              <H tag="button" onClick={onSend}
                baseStyle={{ ...st("width:34px;height:34px;border:none;border-radius:8px;display:flex;align-items:center;justify-content:center;cursor:pointer;flex:none;transition:background .12s"), background: ACCENT, opacity: sending ? 0.5 : 1 }}
                hoverStyle={{ background: `color-mix(in srgb,${ACCENT} 85%,#000)` }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </H>
            </div>
          </div>
        </div>
      </section>

      {/* pipeline panel — gerçek job'lar */}
      <aside style={st("width:352px;flex:none;border-left:1px solid rgba(20,20,30,.08);background:#fbfbfc;display:flex;flex-direction:column;overflow-y:auto")}>
        <div style={st("padding:16px 20px;border-bottom:1px solid rgba(20,20,30,.07)")}>
          <div style={st("font-size:13.5px;font-weight:600;letter-spacing:-.01em")}>Live production</div>
          <div style={st("font-size:12px;color:#9a9aa2;margin-top:1px")}>Every step, in plain sight</div>
        </div>
        <div style={st("padding:20px")}>
          {realJobs.length > 0 ? (
            <div style={st("display:flex;flex-direction:column;gap:16px")}>
              {realJobs.map((job) => <RealJobCard key={job.id} job={job} />)}
            </div>
          ) : (
            <div style={st("text-align:center;padding:40px 10px;color:#9a9aa2")}>
              <div style={st("width:44px;height:44px;border-radius:12px;background:rgba(20,20,30,.05);display:flex;align-items:center;justify-content:center;margin:0 auto 14px")}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b4b4bc" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M4 12h16M4 17h10" /></svg>
              </div>
              <div style={st("font-size:13px;line-height:1.5;max-width:200px;margin:0 auto")}>Nothing in production yet. Send a request and you'll see every step here — live.</div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function RealJobCard({ job }: { job: RealJob }) {
  const color = job.status === "completed" ? "#2f9e6b" : job.status === "failed" ? "#c0392b" : ACCENT;
  return (
    <div style={st("border:1px solid rgba(20,20,30,.08);border-radius:11px;padding:13px 14px;background:#fff")}>
      <div style={st("display:flex;align-items:center;gap:8px;margin-bottom:6px")}>
        {job.status === "running" || job.status === "queued" ? (
          <span style={{ width: 13, height: 13, borderRadius: "50%", border: "2px solid #e6e6ea", borderTopColor: color, animation: "dcspin .7s linear infinite", display: "block", flex: "none" }} />
        ) : job.status === "completed" ? (
          <span style={{ ...st("width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:none"), background: color }}>
            <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7" /></svg>
          </span>
        ) : (
          <span style={{ ...st("width:16px;height:16px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex:none"), background: color }}>!</span>
        )}
        <span style={st("font-size:13.5px;font-weight:600;color:#14141b")}>{job.label}{job.code ? ` · ${job.code}` : ""}</span>
      </div>
      <div style={{ ...st("font-size:12px;line-height:1.4;padding-left:21px"), color: job.status === "failed" ? "#c0392b" : "#6b6b76" }}>{job.error ?? job.stage}</div>
      {job.qa && (
        <div style={st("padding-left:21px;margin-top:6px")}>
          <span style={{ ...st("font-size:11px;font-weight:600;border-radius:5px;padding:2px 7px"), color: job.qa.pass ? "#278a52" : "#c07a2a", background: job.qa.pass ? "color-mix(in srgb,#2f9e6b 12%,#fff)" : "color-mix(in srgb,#c98a1a 14%,#fff)" }}>
            {job.qa.pass ? "✓ QA passed" : `⚠ QA: ${job.qa.decision}`}
          </span>
        </div>
      )}
    </div>
  );
}

function AgentIcon() {
  return (
    <div style={{ ...st("width:26px;height:26px;border-radius:7px;display:flex;align-items:center;justify-content:center;flex:none;margin-top:1px"), background: `color-mix(in srgb,${ACCENT} 12%,#fff)` }}>
      <svg width="15" height="15" viewBox="0 0 24 24" fill={ACCENT}><path d="M12 4l1.4 4.8L18 10l-4.6 1.2L12 16l-1.4-4.8L6 10l4.6-1.2z" /></svg>
    </div>
  );
}

// ── FORMATS ──────────────────────────────────────────────────────────────
// İki kart: Image / Video. Video, UGC tiplerini + custom senaryoları + custom
// video tiplerini + sabit "Talking Head Avatar"ı kapsar. Sağ üstte "+ Yeni Tip"
// Creative Builder'a gider (image → Image tab, video → Flow tab).

type FormatCard = { name: string; desc: string; grad: string };

// isme göre dedup — aynı kod iki kez fetch edilirse (StrictMode çift-mount vb.) yığılma olmasın
function dedupByName(cards: FormatCard[]): FormatCard[] {
  const seen = new Set<string>();
  return cards.filter((c) => (seen.has(c.name) ? false : (seen.add(c.name), true)));
}

export function FormatsPage({ productId }: { productId: string }) {
  const navigate = useNavigate();
  const onPick = (name: string) => navigate(`/products/${productId}/studio?prefill=${encodeURIComponent(`Make a ${name} for women 35+`)}`);
  const [kind, setKind] = useState<"image" | "video" | null>(null);
  const [imageTypes, setImageTypes] = useState<FormatCard[] | null>(null);
  const [videoTypes, setVideoTypes] = useState<FormatCard[] | null>(null);

  useEffect(() => {
    setKind(null);
    setImageTypes(null);
    setVideoTypes(null);
    api.listCustomTypes(productId, "image").then((r) => {
      const customs = (r.custom_types ?? []).map((ct: any, i: number) => ({
        name: ct.name, desc: "Özel görsel tipi.", grad: GRADIENTS[i % GRADIENTS.length]!,
      }));
      const builtins = ALL_TYPES.map((t, i) => ({ name: t, desc: "Hazır görsel tipi.", grad: GRADIENTS[i % GRADIENTS.length]! }));
      setImageTypes(dedupByName([...builtins, ...customs]));
    }).catch(() => setImageTypes([]));

    Promise.all([
      api.listCustomTypes(productId, "video").catch(() => ({ ok: true, custom_types: [] })),
      api.listUGCScenarios(productId).catch(() => ({ ok: true, ugc_scenarios: [] })),
    ]).then(([customRes, scenarioRes]) => {
      const builtins = BUILTIN_UGC_TYPES.map((t, i) => ({ ...t, grad: GRADIENTS[i % GRADIENTS.length]! }));
      const scenarios = (scenarioRes.ugc_scenarios ?? []).map((s: any, i: number) => ({
        name: s.name, desc: "Custom UGC senaryosu.", grad: GRADIENTS[(i + 2) % GRADIENTS.length]!,
      }));
      const customVideo = (customRes.custom_types ?? []).map((ct: any, i: number) => ({
        name: ct.name, desc: "Özel video tipi.", grad: GRADIENTS[(i + 4) % GRADIENTS.length]!,
      }));
      const avatar = { ...TALKING_HEAD_AVATAR, grad: GRADIENTS[7]! };
      const longStory = { ...LONG_STORY, grad: GRADIENTS[3]! };
      const voxAd = { ...VOX_AD, grad: GRADIENTS[5]! };
      const brollAd = { ...BROLL_AD, grad: GRADIENTS[6]! };
      const hookBodyAvatar = { ...HOOK_BODY_AVATAR, grad: GRADIENTS[2]! };
      setVideoTypes(dedupByName([...builtins, ...scenarios, ...customVideo, avatar, longStory, voxAd, brollAd, hookBodyAvatar]));
    }).catch(() => setVideoTypes([]));
  }, [productId]);

  if (!kind) {
    return (
      <div style={st("flex:1;overflow-y:auto")}>
        <div style={st("max-width:900px;margin:0 auto;padding:40px 48px 64px")}>
          <div style={st("margin-bottom:26px")}>
            <h1 style={st("margin:0 0 6px;font-size:24px;font-weight:600;letter-spacing:-.02em")}>Formats</h1>
            <p style={st("margin:0;font-size:14.5px;color:#6b6b76")}>Ne üretmek istiyorsun?</p>
          </div>
          <div style={st("display:grid;grid-template-columns:repeat(2,1fr);gap:16px")}>
            <H onClick={() => setKind("image")}
              baseStyle={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:13px;padding:32px;cursor:pointer;transition:all .15s;text-align:center")}
              hoverStyle={st("border-color:rgba(20,20,30,.18);box-shadow:0 4px 16px rgba(20,20,30,.07);transform:translateY(-2px)")}
            >
              <div style={st("font-size:34px;margin-bottom:10px")}>🖼️</div>
              <div style={st("font-size:16px;font-weight:600;color:#14141b")}>Image</div>
              <div style={st("font-size:12.5px;color:#8a8a93;margin-top:4px")}>{imageTypes ? `${imageTypes.length} tip` : "…"}</div>
            </H>
            <H onClick={() => setKind("video")}
              baseStyle={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:13px;padding:32px;cursor:pointer;transition:all .15s;text-align:center")}
              hoverStyle={st("border-color:rgba(20,20,30,.18);box-shadow:0 4px 16px rgba(20,20,30,.07);transform:translateY(-2px)")}
            >
              <div style={st("font-size:34px;margin-bottom:10px")}>🎬</div>
              <div style={st("font-size:16px;font-weight:600;color:#14141b")}>Video</div>
              <div style={st("font-size:12.5px;color:#8a8a93;margin-top:4px")}>{videoTypes ? `${videoTypes.length} tip` : "…"}</div>
            </H>
          </div>
        </div>
      </div>
    );
  }

  const list = kind === "image" ? imageTypes : videoTypes;
  return (
    <div style={st("flex:1;overflow-y:auto")}>
      <div style={st("max-width:1080px;margin:0 auto;padding:40px 48px 64px")}>
        <div style={st("display:flex;align-items:center;justify-content:space-between;margin-bottom:26px")}>
          <div>
            <button onClick={() => setKind(null)} style={{ ...st("font-size:12.5px;border:none;background:none;cursor:pointer;margin-bottom:8px;padding:0"), color: "#8a8a93" }}>← Formats</button>
            <h1 style={st("margin:0 0 6px;font-size:24px;font-weight:600;letter-spacing:-.02em")}>{kind === "image" ? "Image" : "Video"} Formats</h1>
            <p style={st("margin:0;font-size:14.5px;color:#6b6b76")}>Pick one to start a request with it.</p>
          </div>
          <H tag="button" onClick={() => navigate(`/products/${productId}/builder?tab=${kind === "image" ? "image" : "flow"}`)}
            baseStyle={{ ...st("display:flex;align-items:center;gap:6px;padding:9px 15px;border:none;border-radius:8px;color:#fff;font-family:inherit;font-size:13px;font-weight:500;cursor:pointer;flex:none;white-space:nowrap;transition:background .12s"), background: ACCENT }}
            hoverStyle={{ background: `color-mix(in srgb,${ACCENT} 85%,#000)` }}
          >
            + Yeni Tip
          </H>
        </div>
        {!list ? (
          <div style={st("text-align:center;color:#9a9aa2;padding:40px 0")}>Yükleniyor…</div>
        ) : (
          <div style={st("display:grid;grid-template-columns:repeat(auto-fill,minmax(228px,1fr));gap:16px")}>
            {list.map((g) => (
              <H key={g.name} onClick={() => onPick(g.name)}
                baseStyle={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:13px;overflow:hidden;cursor:pointer;transition:all .15s")}
                hoverStyle={st("border-color:rgba(20,20,30,.18);box-shadow:0 4px 16px rgba(20,20,30,.07);transform:translateY(-2px)")}
              >
                <div style={{ ...st("position:relative;aspect-ratio:16/11;display:flex;align-items:center;justify-content:center"), background: g.grad }}>
                  <div style={st("width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,.85);display:flex;align-items:center;justify-content:center;box-shadow:0 2px 10px rgba(20,20,30,.12)")}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#2a2a33"><path d="M8 5v14l11-7z" /></svg>
                  </div>
                </div>
                <div style={st("padding:14px 15px 16px")}>
                  <div style={st("font-size:14px;font-weight:600;color:#14141b;margin-bottom:4px")}>{g.name}</div>
                  <div style={st("font-size:12.5px;line-height:1.5;color:#6b6b76;margin-bottom:11px")}>{g.desc}</div>
                  <div style={{ ...st("display:flex;align-items:center;gap:5px;font-size:12.5px;font-weight:500"), color: ACCENT }}>
                    Use this format
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                  </div>
                </div>
              </H>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

